import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  HostListener,
  OnChanges,
  forwardRef,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subject,
  take,
  takeUntil
} from 'rxjs';


export interface SelectOption {
  value: any;
  label: string;
}

@Component({
  selector: 'app-metro-form-select',
  templateUrl: './metro-form-select.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetroFormSelectComponent),
      multi: true
    }
  ]
})
export class MetroFormSelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  private destroy$ = new Subject<void>();

  @Input() label = '';
  @Input() placeholder = '';
  @Input() selectedBackgroundClass: string = '';

  @Input() set options(value: SelectOption[]) {
    this._options.next(value);
  }
  private _options = new BehaviorSubject<SelectOption[]>([]);
  public options$: Observable<SelectOption[]> = this._options.asObservable().pipe(
    filter(opt => Array.isArray(opt)),
    distinctUntilChanged(),
    takeUntil(this.destroy$)
  );

  private searchTerm$ = new BehaviorSubject<string>('');
  set searchTerm(value: string) {
    this.searchTerm$.next(value);
  }
  get searchTerm(): string {
    return this.searchTerm$.value;
  }

  private selectionChange$ = new BehaviorSubject<number>(0);
  private emitSelectionChange() {
    this.selectionChange$.next(this.selectionChange$.value + 1);
  }

  filteredOptions$: Observable<SelectOption[]> = combineLatest([
    this.searchTerm$.pipe(debounceTime(10)),
    this.options$,
    this.selectionChange$
  ]).pipe(
    map(([search, opt]) => {
      const term = (search || '').toLowerCase();
      let filtered = opt.filter(o => o.label.toLowerCase().includes(term));

      if (this.multiple) {
        const selectedValues = new Set(this.selected.map(s => s.value));
        filtered = filtered.filter(o => !selectedValues.has(o.value));
      }

      return filtered;
    })
  );

  @Input() disabled = false;
  @Input() multiple = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() error?: string;
  @Input() reset = false;
  @Input() autocomplete = 'off';

    @Input() colorVariant: 'default' | 'brown-alt' = 'default';

  @Input() allowSearch: boolean = true;
  @Input() allowClear: boolean = true;
  @Output() selectionChange = new EventEmitter<any | any[]>();
  @Output() Select = new EventEmitter<any | any[]>();

  @ViewChild('inputRef') inputRef?: ElementRef<HTMLInputElement>;

  dropdownOpen = false;

  private get value(): any | any[] {
    return this.multiple ? this.selected.map(opt => opt.value) : this.selected[0]?.value;
  }

  private selected: SelectOption[] = [];

  // Propiedad computada para determinar la clase de fondo de los items seleccionados
  get selectedBackgroundClasses(): string {
    return this.selectedBackgroundClass || 'bg-[#344b65]';
  }

  onChange: any = () => {};
  onTouched: any = () => {};
  touched = false;

  constructor(private elementRef: ElementRef) {
    this.options$.pipe(takeUntil(this.destroy$)).subscribe(opts => {
      const allowed = new Set(opts.map(o => o.value));
      const nextSelected = this.selected.filter(s => allowed.has(s.value));
      if (nextSelected.length !== this.selected.length) {
        this.selected = nextSelected;
        this.emitSelectionChange();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reset']?.currentValue === true) {
      this.clearSelection();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: any | any[]): void {
    const isReset = value == null || (Array.isArray(value) && value.length === 0);

    if (isReset) {
      this.searchTerm = '';
      this.selected = [];
      this.emitSelectionChange();
      return;
    }

    this.options$.pipe(filter(opt => opt.length > 0), take(1)).subscribe(options => {
      if (this.multiple) {
        const valueArray = Array.isArray(value) ? value : [value];
        this.selected = options.filter(opt => valueArray.includes(opt.value));
      } else {
        const selectedOption = options.find(opt => opt.value === value);

        this.searchTerm = (selectedOption && this.allowSearch) ? selectedOption.label : '';

        this.selected = selectedOption ? [selectedOption] : [];
      }
      this.emitSelectionChange();
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  getSelectedValuesArray(): string[] {
    if (this.multiple) {
      return Array.isArray(this.value) ? this.value : [];
    } else {
      return typeof this.value === 'string' && this.value ? [this.value] : [];
    }
  }

  selectOption(option: SelectOption) {
    this.markAsTouched();
    if (this.disabled) return;

    if (this.multiple) {
      const isSelected = this.selected.some(s => s.value === option.value);
      isSelected ? this.removeValue(option) : this.addValue(option);
      this.searchTerm = '';
    } else {
      this.selected = [option];

      this.searchTerm = this.allowSearch ? option.label : '';
      this.dropdownOpen = false;
      this.emitSelectionChange();
    }

    this.onChange(this.value);
    this.Select.emit(this.value);
    this.selectionChange.emit(this.value);
  }

  clearSelection() {
    this.markAsTouched();
    this.selected = [];
    this.searchTerm = '';
    this.emitSelectionChange();
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  private addValue(option: SelectOption) {
    if (!this.selected.some(s => s.value === option.value)) {
      this.selected.push(option);
      this.emitSelectionChange();
    }
  }

  private removeValue(option: SelectOption) {
    const before = this.selected.length;
    this.selected = this.selected.filter(s => s.value !== option.value);
    if (this.selected.length !== before) {
      this.emitSelectionChange();
      // Notificar el cambio al formulario
      this.onChange(this.value);
      this.selectionChange.emit(this.value);
    }
  }

  removeOption(option: SelectOption, event: MouseEvent) {
    event.stopPropagation();
    this.markAsTouched();
    if (this.disabled || !Array.isArray(this.value)) return;

    this.removeValue(option);
  }

  toggleDropdown() {
    if (this.disabled) return;
    this.dropdownOpen = true;
    setTimeout(() => this.inputRef?.nativeElement.focus(), 0);
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if ((event.key === 'Backspace' || event.key === 'Delete') && !this.multiple && this.allowSearch && this.value) {
      this.clearSelection();
      event.preventDefault();
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.dropdownOpen = false;
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget)) {
      this.markAsTouched();
    }
  }

  isSelected(option: SelectOption): boolean {
  return this.selected.some(s => s.value === option.value);
}

}


